import type { BlogPost } from './types';

export const post: BlogPost = {
  slug: 'the-cold-streak-survival-guide',
  title: 'The cold streak survival guide',
  excerpt:
    'Losing five in a row is not a sign. It is a Tuesday. Here is the math that proves it and the checklist that keeps it from becoming a crater.',
  author: 'Sloane',
  date: '2026-07-12',
  category: 'Bankroll',
  evergreen: true,
  tags: ['cold streak', 'bankroll', 'variance', 'discipline'],
  body: `# The cold streak survival guide

Let me start with the sentence I say to my sister roughly once a month: losing five in a row does not mean anything is broken.

Here is the math. Take a genuinely good bettor, someone hitting 55% long-term, which is better than almost everyone you follow. The odds of that person losing five straight bets are about 1.8%. Sounds rare. Now let them place four hundred bets over a season. A 1-in-55 event, given four hundred chances to start, is not a possibility. It is a scheduled appearance. The cold streak is coming the way winter is coming, and the only open question is what your bankroll looks like when it leaves.

## The streak was always coming

So this is not a pep talk. It is a checklist.

## The checklist

**Check the process, not the results.** Go back through the notes you are hopefully keeping and reread why you made each bet. If the reasons still hold up, you got outrun by variance, full stop. A closer blew it. A kicker missed. A backdoor cover happened to someone who did not deserve it, which is to say, you. Bad outcomes on good reasoning require zero changes. Bad reasoning that happened to be losing this week too, that is worth your attention.

**Cut the unit, do not raise it.** The instinct after four losses is to bet bigger, because one win at double size erases everything. That instinct has a name, [chasing](/blog/bankroll-management-for-people-who-hate-math), and it is how a bad week applies for a promotion. Do the opposite. Drop to half units until the fog lifts. If your reads are actually fine, you lose nothing but bragging rights. If they are not, you just paid half price to find out.

**Shrink the card.** Cold streaks breed volume, because every game starts looking like the one that gets you even. Flip it. One bet a day, your single best number, or no bet at all. "No bet" is a position. It is the only position with a guaranteed return of exactly zero, which during a cold streak is a top-decile performance.

**Take the day off, on purpose.** Not as punishment. As calibration. One full slate watched with nothing riding on it reminds your brain what the games look like when they are not personal. The slate does not miss you. It will be there Thursday.

## Do not rebuild at midnight

And the one thing not to do: do not rebuild the whole system at midnight. Torching a decent process because of one bad fortnight is how people end up with a new betting philosophy every month and a lifetime record that never improves.

The streak ends. They all do. The bettors who are still around afterward are the ones who got boring fast and stayed boring until it passed.

Boring is a strategy. Frankie hates it until the fifth straight loss, then calls half units "temporary federal oversight." Can't even argue with the branding.`,
};
